import { Tab, Tabs } from "@mui/material"
import classNames from "classnames"
import AppInfoHeader from "components/AppInfoHeader"
import { AppInstallBtn } from "components/AppInstallBtn"
import AppStats from "components/AppStats"
import BackBtn from "components/BackBtn"
import Icon from "components/Icon"
import { Column, Row } from "components/Layout"
import Loader from "components/Loader"
import ScreenshotGallery from "components/ScreenshotGallery"
import PageLoader from "containers/PageLoader"
import useScrollTopSnap from "hooks/useScrollTopSnap"
import * as gamesAPI from "modules/gamesAPI"
import { openUrl } from "modules/nativeAPI"
import utils from "modules/utils"
import { useCallback, useEffect, useRef, useState } from "react"
import { useParams } from "react-router-dom"
import { useAsync } from "react-use"
import colors from "styles/colors"
import css from "./AppInfo.module.scss"

const appInfoTabs = {
  GAME_INFO: 'game info',
  DEVELOPER_INFO: 'developer info',
  RELATED_CONTENT: 'related content'
}

export function AppInfoPage() {
  const { appId } = useParams()
  const headerRef = useRef(null)
  const [isHeaderRefInitialized, setIsHeaderRefInitialized] = useState(false)
  const headerStuck = useScrollTopSnap(headerRef, isHeaderRefInitialized)

  const setRef = useCallback(node => {
    if (node) {
      setIsHeaderRefInitialized(true)
    }

    headerRef.current = node
  }, [])




  // State
  const [currentTab, setTab] = useState(appInfoTabs.GAME_INFO)
  const [enterPageTime] = useState(new Date())

  // Load app info from API
  const {
    loading,
    error,
    value: app,
  } = useAsync(() => gamesAPI.getAppInfo(appId))

  useEffect(() => {
    if (app) {
      
    }

    return () => {
      if (app) {
      
      }
    }
  }, [app, enterPageTime]);


  if (loading) return <Loader />
  if (error) throw error
  if (!app) return <Row align="mc">No App Found</Row>

  const handleTabChange = (e, tabName) => {
    setTab(tabName)
  }

  // Extract Assets
  const icon = app.locale.icon
  const headerImage = app.locale.headerImage || app.locale.screenshots[0]

  return (
    <div className={css.appInfo}>
      <BackBtn className={css.backBtn} />

      <Row
        className={css.coverPhoto}
        style={{ backgroundImage: `url('${headerImage}')` }}
      />

      <div ref={setRef} className={classNames(css.header, {
        [css.top]: headerStuck
      })}>
        <Column padding="lg 1.5rem" gap="lg">
          <AppInfoHeader
            icon={icon}
            name={app.locale.title}
            developer={app.developer}
            primaryCategory={app.primaryCategory}
            appOtherCategories={app.otherCategories}
            tags={app.tags}
            offersIAP={app.offersIap}
          />

          <AppStats
            className={css.appStats}
            score={app.score}
            reviews={app.ratings}
            size={app.appDelivery?.size}
            downloads={parseInt(app.maxInstalls)}
          />
        </Column>
      </div>

      <ScreenshotGallery images={app.locale.screenshots} app={app} />

      <div className={css.tabs}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          // variant="scrollable"
          centered
        >
          <Tab label="Game Info" value={appInfoTabs.GAME_INFO} />
          <Tab label="Developer Info" value={appInfoTabs.DEVELOPER_INFO} />
          {/* <Tab label="Related Content" value={appInfoTabs.RELATED_CONTENT} /> */}
        </Tabs>
      </div>

      {currentTab === appInfoTabs.GAME_INFO &&
        <Column
          className={css.tabContent}
          padding="1.5rem lg"
        >
          <h4>About {app.locale.title}</h4>

          <div
            dangerouslySetInnerHTML={{ __html: app.locale.descriptionHtml }}
            margin="tb-lg"
          ></div>

          <h4>What's new</h4>
          <p>
            {app.locale.title} v{app.version} Update
          </p>
          <div
            dangerouslySetInnerHTML={{ __html: app.locale.recentChanges }}
            margin="bottom-xl"
          ></div>

          <h4>Additional Info</h4>
          <Column padding="sm 0">
            {[
              ["version", "Version", app.version],
              ["downloads", "Downloads", `${utils.abbreviateNumber(app.maxInstalls)}+ Downloads`],
              app.appDelivery?.size && ["size", "App Size", utils.humanFileSize(app.appDelivery?.size)],
              // ["os", "Required OS", "???"],
              // ["purchases", "In-App Purchases", "???"],
              ["publisher", "Offered By", app.developer.name],
            ]
              .filter(e => e) // Remove empty
              .map(([icon, label, val], i) => (
                <Row key={label} align="mc" padding="sm 0" gap="sm">
                  <Icon
                    name={icon}
                    bg="circle"
                    size={35}
                    color={colors.textLight}
                    bgColor={colors.cardsBackground}
                  />
                  <h5 col="1" style={{ color: colors.textLight }}>
                    {label}
                  </h5>
                  <h5 style={{ color: colors.textMedium }} className="ellipsis">
                    {val}
                  </h5>
                </Row>
              ))}
          </Column>
        </Column>}

      {currentTab === appInfoTabs.DEVELOPER_INFO && <Column
        className={css.tabContent}
        padding="1.5rem lg"
      >
        <h4>Developer Info</h4>
        <Column padding="xxl 0" gap="xxl">
          {[
            // ["play", "Developer", app.developer.name],
            ["website", "Website", <u onClick={() => openUrl(app.developer.website)}>{app.developer.website}</u>],
            ["email", "Email", <u onClick={() => openUrl(`mailto: ${app.developer.email}`)}>{app.developer.email}</u>],
            ["address", "Address", app.developer.address],
            ["privacy", "Privacy Policy", <u onClick={() => openUrl(app.locale.privacyPolicy)}>{app.locale.privacyPolicy}</u>],
          ].map(([icon, label, val]) => (
            <Row key={label} align="mc" gap="sm">
              <Icon
                name={icon}
                bg="circle"
                size={35}
                color={colors.textLight}
                bgColor={colors.cardsBackground}
              />
              <div col={1} className={css.developerInfoField}>
                <h5 style={{ color: colors.textLight }}>{label}</h5>
                <h5 style={{ color: colors.textMedium }}>{val}</h5>
              </div>
            </Row>
          ))}
        </Column>
      </Column>}

      {currentTab === appInfoTabs.RELATED_CONTENT &&
        <div className={css.tabContent}>
          {currentTab === appInfoTabs.RELATED_CONTENT && <PageLoader appId={appId} />}
        </div>
      }

      <Row align="mt" className={css.installBtn} padding="lg 1.5rem">
        <AppInstallBtn
          appId={app.id}
          packageName={app.packageName}
          versionCode={app.versionCode}
          isPartnerApp={app.isPartnerApp}
          type="large"
          app={app}
        />
      </Row>
    </div>
  )
}
